import { deserialize, DocumentObject, NewDocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import {
  CategoriesMeta,
  Category,
  CategoryCreate,
  CategoryResourceType,
  CategorySearchFilter,
  CategoryUpdate,
} from '~/types/category';
import { Company } from '~/types/company';
import { MetaDataCursorPage, pagedMetaSchema } from '~/types/jsonapi';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';

const CategoriesPageSize = 20;

const defaultQuerySorting = (): SortInfo => ({
  sort: 'name',
  direction: 'asc',
});

export const createCategory = async (category: CategoryCreate) => {
  const payload = serialize(category, CategoryResourceType, {
    relationships: ['company'],
  });

  const { data } = await backendApiGateway.post<NewDocumentObject>('/api/v1/categories', payload);

  return deserialize<Category>(data) as Category;
};

export const updateCategory = async ({ id, update }: { id: Category['id']; update: CategoryUpdate }) => {
  const payload = serialize(
    {
      id,
      ...update,
    },
    CategoryResourceType,
  );

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/categories/${id}`, payload);

  return deserialize<Category>(data) as Category;
};

export const deleteCategory = async (id: Category['id']) => {
  await backendApiGateway.delete<void>(`/api/v1/categories/${id}`);
  return id;
};

export const fetchAllCategories = async (companyId: string): Promise<Category[]> => {
  let allCategories: Category[] = [];
  let fetchedCategories: Category[] = [];

  let cursor: MetaDataCursorPage['cursor'] | null = null;
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);

  const includeBuilder = jsonAPIIncludeBuilder(['subcategories']);
  do {
    const pageBuilder = jsonAPICursorPaginatorBuilder({ size: 100 });
    if (cursor) pageBuilder.cursor(cursor);
    const searchParams = buildJsonAPIQueryParams(filterBuilder, includeBuilder, pageBuilder);

    const { data: results } = await backendApiGateway.get<DocumentObject & { meta: CategoriesMeta }>(
      '/api/v1/categories',
      { params: searchParams },
    );
    fetchedCategories = deserialize<Category>(results) as Category[];
    allCategories = allCategories.concat(fetchedCategories);
    const { page } = pagedMetaSchema.parse(results.meta);
    cursor = page.cursor;
  } while (fetchedCategories.length === 100);

  return allCategories;
};

export const searchCategories = async (
  companyId: Company['id'],
  filters: CategorySearchFilter,
  options: {
    size: number;
    cursor?: string;
  } = { size: CategoriesPageSize },
) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  filterBuilder.ilike('name', filters.search);

  const sortingBuilder = jsonAPISortBuilder([defaultQuerySorting()]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options.size);
  if (options.cursor) paginationParamsBuilder.cursor(options.cursor);

  const searchParams = buildJsonAPIQueryParams(filterBuilder, sortingBuilder, paginationParamsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject & { meta: CategoriesMeta }>('/api/v1/categories', {
    params: searchParams,
  });

  const { page } = pagedMetaSchema.parse(data.meta);

  return {
    data: deserialize<Category>(data) as Category[],
    page,
  };
};

export const fetchCategory = async (id: Category['id']) => {
  const includeBuilder = jsonAPIIncludeBuilder(['subcategories']);
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/categories/${id}`, {
    params: searchParams,
  });

  return deserialize<Category>(data) as Category;
};
